import AuthService from "./AuthService";

/**
 * @namespace com.ang.portal.service
 */

export interface IntegrationRecord {
	id: number;
	name: string;
	keyPrefix: string;
	scopes: string[];
	isActive: boolean;
	createdBy: number | null;
	lastUsedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface CreateIntegrationPayload {
	name: string;
}

export interface CreateIntegrationResponse extends IntegrationRecord {
	apiKey: string;
}

export interface RotateIntegrationResponse {
	id: number;
	apiKey: string;
}

async function parseError(response: Response, fallbackMessage: string): Promise<Error> {
	const error = (await response.json().catch(() => ({ message: fallbackMessage }))) as {
		message?: string;
	};
	return new Error(error.message || fallbackMessage);
}

const KeyManagementService = {
	async getIntegrations(): Promise<IntegrationRecord[]> {
		const response = await AuthService.authenticatedFetch(
			"/api/key-integrations",
		);
		if (!response.ok) {
			throw await parseError(response, "Failed to fetch key integrations");
		}
		const body = (await response.json()) as { integrations: IntegrationRecord[] };
		return body.integrations;
	},

	async createIntegration(
		payload: CreateIntegrationPayload,
	): Promise<CreateIntegrationResponse> {
		const response = await AuthService.authenticatedFetch(
			"/api/key-integrations",
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
		);
		if (!response.ok) {
			throw await parseError(response, "Failed to create integration");
		}
		return response.json() as Promise<CreateIntegrationResponse>;
	},

	async rotateIntegration(id: number): Promise<RotateIntegrationResponse> {
		const response = await AuthService.authenticatedFetch(
			`/api/key-integrations/${id}/rotate`,
			{ method: "POST" },
		);
		if (!response.ok) {
			throw await parseError(response, "Failed to rotate integration key");
		}
		return response.json() as Promise<RotateIntegrationResponse>;
	},

	async revokeIntegration(id: number): Promise<void> {
		const response = await AuthService.authenticatedFetch(
			`/api/key-integrations/${id}`,
			{ method: "DELETE" },
		);
		if (!response.ok) {
			throw await parseError(response, "Failed to revoke integration");
		}
	},
};

export default KeyManagementService;
